import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  CircularProgress,
  InputAdornment,
  Stack,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import {
  hrListEmployees,
  hrCreateEmployee,
  hrUpdateEmployee,
  hrDeleteEmployee,
} from '../BaseURL';

const EMPLOYMENT_TYPES = ['Full-time', 'Part-time', 'Contract', 'Locum', 'Intern'];

const EMPTY_FORM = {
  staff_id: '', full_name: '', gender: '', date_of_birth: '', national_id: '',
  phone: '', email: '', address: '',
  department: '', position: '', employment_type: 'Full-time',
  hire_date: '', end_date: '',
  basic_salary: '', allowances: '', currency: 'GHS',
  bank_name: '', bank_branch: '', bank_account_no: '',
  ssnit_no: '', tin_no: '',
  next_of_kin_name: '', next_of_kin_phone: '', next_of_kin_relation: '',
};

const formatCurrency = (n, ccy = 'GHS') =>
  `${ccy} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Employees = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const res = await axios.get(hrListEmployees);
      if (res.data?.success) setRows(res.data.employees || []);
    } catch (err) {
      console.error('Failed to load employees', err);
      Swal.fire({ icon: 'error', title: 'Failed to load', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEmployees(); }, []);
  useEffect(() => { setPage(0); }, [search]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      [r.full_name, r.staff_id, r.email, r.phone, r.department, r.position]
        .filter(Boolean).join(' ').toLowerCase().includes(q)
    );
  }, [rows, search]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      ...EMPTY_FORM,
      ...row,
      date_of_birth: row.date_of_birth ? String(row.date_of_birth).slice(0, 10) : '',
      hire_date:     row.hire_date     ? String(row.hire_date).slice(0, 10)     : '',
      end_date:      row.end_date      ? String(row.end_date).slice(0, 10)      : '',
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const handleField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    if (!form.full_name.trim()) {
      Swal.fire({ icon: 'warning', title: 'Full name is required' });
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(hrUpdateEmployee(editing.id), form);
      } else {
        await axios.post(hrCreateEmployee, form);
      }
      await Swal.fire({
        icon: 'success',
        title: editing ? 'Employee updated' : 'Employee added',
        timer: 1200, showConfirmButton: false,
      });
      closeDialog();
      fetchEmployees();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Save failed', text: err.response?.data?.message || err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (row) => {
    const r = await Swal.fire({
      icon: 'warning',
      title: `Deactivate ${row.full_name}?`,
      text: 'The record is preserved for payroll history; the employee is just marked inactive.',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, deactivate',
    });
    if (!r.isConfirmed) return;
    try {
      await axios.delete(hrDeleteEmployee(row.id));
      fetchEmployees();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed', text: err.message });
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f1fcf9 0%, #e8f5e9 100%)',
      p: 2.5,
    }}>
      <Paper sx={{
        p: 2,
        borderRadius: 2.5,
        boxShadow: '0 2px 12px rgba(17, 153, 142, 0.06)',
        background: 'white',
        border: '1px solid #e8f5e9',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: 2,
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 14px rgba(17, 153, 142, 0.35)',
            }}>
              <GroupIcon sx={{ color: 'white', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1b5e20', lineHeight: 1.2 }}>
                Employees
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {filtered.length} employee{filtered.length === 1 ? '' : 's'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search by name, ID, department..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ color: '#11998e', mr: 1, fontSize: 20 }} /> }}
              sx={{
                minWidth: 280,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2, bgcolor: '#f1fcf9',
                  '& fieldset': { borderColor: '#c8e6c9' },
                  '&:hover fieldset': { borderColor: '#11998e' },
                  '&.Mui-focused fieldset': { borderColor: '#11998e', borderWidth: 2 },
                },
              }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAdd}
              sx={{
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 2.5,
                boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #0e8679 0%, #2ed968 100%)' },
              }}
            >
              Add Employee
            </Button>
          </Box>
        </Box>

        <TableContainer sx={{ borderRadius: 2, border: '1px solid #e8f5e9', overflow: 'hidden' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                {['Name', 'Staff ID', 'Department', 'Position', 'Employment', 'Gross / mo', 'Status', 'Actions'].map((h, i) => (
                  <TableCell key={h} align={i === 7 ? 'right' : 'left'} sx={{
                    color: 'white', fontWeight: 700,
                    fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px',
                  }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 5 }}>
                  <CircularProgress size={32} sx={{ color: '#11998e' }} />
                </TableCell></TableRow>
              ) : paged.length === 0 ? (
                <TableRow><TableCell colSpan={8} sx={{ p: 0, border: 'none' }}>
                  <Box sx={{
                    textAlign: 'center', py: 6,
                    background: 'linear-gradient(135deg, #f1fcf9 0%, #e8f5e9 100%)',
                    border: '1px dashed #c8e6c9',
                  }}>
                    <GroupIcon sx={{ fontSize: 48, color: '#11998e', opacity: 0.5, mb: 1 }} />
                    <Typography variant="h6" sx={{ color: '#1b5e20', fontWeight: 600, mb: 0.5 }}>
                      {search ? 'No matching employees' : 'No employees yet'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {search ? 'Try a different search' : 'Click "Add Employee" to register your first staff member'}
                    </Typography>
                  </Box>
                </TableCell></TableRow>
              ) : paged.map((row, i) => (
                <TableRow key={row.id} sx={{
                  bgcolor: i % 2 === 0 ? 'white' : '#f8fdfb',
                  '&:hover': { bgcolor: '#e8f5e9' },
                  borderBottom: '1px solid #e8f5e9',
                }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{
                        width: 36, height: 36, borderRadius: '50%',
                        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontWeight: 700, fontSize: '0.75rem', flexShrink: 0,
                      }}>
                        {row.full_name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 600, color: '#1b5e20' }}>{row.full_name}</Typography>
                        {row.email && (
                          <Typography variant="caption" color="text.secondary">{row.email}</Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', color: '#37474f' }}>{row.staff_id || '—'}</TableCell>
                  <TableCell>{row.department || '—'}</TableCell>
                  <TableCell>{row.position || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.employment_type || '—'}
                      sx={{
                        bgcolor: '#e8f5e9', color: '#1b5e20',
                        fontWeight: 600, border: '1px solid #c8e6c9',
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1b5e20' }}>
                    {formatCurrency(Number(row.basic_salary || 0) + Number(row.allowances || 0), row.currency)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.is_active ? 'Active' : 'Inactive'}
                      sx={{
                        bgcolor: row.is_active ? '#e8f5e9' : '#fff0f0',
                        color:   row.is_active ? '#1b5e20' : '#b71c1c',
                        fontWeight: 700,
                        border: `1px solid ${row.is_active ? '#c8e6c9' : '#ffcdd2'}`,
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(row)} sx={{ color: '#11998e' }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {row.is_active === 1 && (
                      <IconButton size="small" onClick={() => handleDeactivate(row)} sx={{ color: '#d32f2f' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
          sx={{ borderTop: '1px solid #e8f5e9' }}
        />
      </Paper>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}>
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{
            p: 2,
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            color: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
            borderRadius: '12px 12px 0 0',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 40, height: 40, borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid rgba(255,255,255,0.3)',
              }}>
                <GroupIcon />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                  {editing ? 'Edit Employee' : 'Add New Employee'}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Personal · employment · salary · banking · next of kin
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={closeDialog} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ bgcolor: '#f1fcf9', p: 2.5 }}>
          <Stack spacing={2}>
            <FormSection title="Personal Information">
              <Field label="Full Name *" value={form.full_name} onChange={v => handleField('full_name', v)} />
              <Field label="Gender" value={form.gender} onChange={v => handleField('gender', v)} select options={['Male', 'Female', 'Other']} />
              <Field label="Date of Birth" value={form.date_of_birth} onChange={v => handleField('date_of_birth', v)} type="date" />
              <Field label="National / Ghana Card" value={form.national_id} onChange={v => handleField('national_id', v)} />
              <Field label="Phone" value={form.phone} onChange={v => handleField('phone', v)} />
              <Field label="Email" value={form.email} onChange={v => handleField('email', v)} />
              <Field label="Address" value={form.address} onChange={v => handleField('address', v)} fullWidth />
            </FormSection>

            <FormSection title="Employment">
              <Field label="Staff ID (link to login)" value={form.staff_id} onChange={v => handleField('staff_id', v)} hint="Matches staff.staff_id when this employee also has a system login" />
              <Field label="Department" value={form.department} onChange={v => handleField('department', v)} />
              <Field label="Position" value={form.position} onChange={v => handleField('position', v)} />
              <Field label="Employment Type" value={form.employment_type} onChange={v => handleField('employment_type', v)} select options={EMPLOYMENT_TYPES} />
              <Field label="Hire Date" value={form.hire_date} onChange={v => handleField('hire_date', v)} type="date" />
              <Field label="End Date" value={form.end_date} onChange={v => handleField('end_date', v)} type="date" hint="Leave blank if currently employed" />
            </FormSection>

            <FormSection title="Salary">
              <Field label="Basic Salary" value={form.basic_salary} onChange={v => handleField('basic_salary', v)} type="number"
                InputProps={{ startAdornment: <InputAdornment position="start">{form.currency || 'GHS'}</InputAdornment> }} />
              <Field label="Allowances (monthly total)" value={form.allowances} onChange={v => handleField('allowances', v)} type="number"
                InputProps={{ startAdornment: <InputAdornment position="start">{form.currency || 'GHS'}</InputAdornment> }} />
              <Field label="Currency" value={form.currency} onChange={v => handleField('currency', v)} select options={['GHS', 'USD', 'EUR', 'GBP', 'NGN']} />
            </FormSection>

            <FormSection title="Bank & Statutory">
              <Field label="Bank Name" value={form.bank_name} onChange={v => handleField('bank_name', v)} />
              <Field label="Bank Branch" value={form.bank_branch} onChange={v => handleField('bank_branch', v)} />
              <Field label="Account Number" value={form.bank_account_no} onChange={v => handleField('bank_account_no', v)} />
              <Field label="SSNIT Number" value={form.ssnit_no} onChange={v => handleField('ssnit_no', v)} />
              <Field label="TIN" value={form.tin_no} onChange={v => handleField('tin_no', v)} />
            </FormSection>

            <FormSection title="Next of Kin">
              <Field label="Name" value={form.next_of_kin_name} onChange={v => handleField('next_of_kin_name', v)} />
              <Field label="Phone" value={form.next_of_kin_phone} onChange={v => handleField('next_of_kin_phone', v)} />
              <Field label="Relationship" value={form.next_of_kin_relation} onChange={v => handleField('next_of_kin_relation', v)} />
            </FormSection>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2, bgcolor: '#f1fcf9', gap: 1 }}>
          <Button onClick={closeDialog} disabled={saving} sx={{ color: '#607d8b', fontWeight: 600, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !form.full_name.trim()}
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <SaveIcon />}
            sx={{
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 3,
              boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #0e8679 0%, #2ed968 100%)' },
              '&.Mui-disabled': { background: '#cbd5e1', color: 'white' },
            }}
          >
            {saving ? 'Saving…' : editing ? 'Update Employee' : 'Save Employee'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const FormSection = ({ title, children }) => (
  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #c8e6c9', bgcolor: 'white' }}>
    <Typography variant="subtitle2" sx={{
      fontWeight: 700, color: '#1b5e20', mb: 1.5,
      display: 'flex', alignItems: 'center', gap: 1,
    }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#11998e' }} />
      {title}
    </Typography>
    <Divider sx={{ mb: 1.5, borderColor: '#e8f5e9' }} />
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
      gap: 1.5,
    }}>
      {children}
    </Box>
  </Paper>
);

const Field = ({ label, value, onChange, type = 'text', select = false, options = [], fullWidth = false, hint, InputProps }) => (
  <Box sx={{ gridColumn: fullWidth ? '1 / -1' : 'auto' }}>
    <TextField
      fullWidth
      size="small"
      label={label}
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      select={select}
      InputLabelProps={type === 'date' ? { shrink: true } : undefined}
      InputProps={InputProps}
      helperText={hint}
      FormHelperTextProps={{ sx: { mx: 0.5, fontSize: '0.68rem' } }}
      sx={{
        '& .MuiOutlinedInput-root': {
          '&:hover fieldset': { borderColor: '#11998e' },
          '&.Mui-focused fieldset': { borderColor: '#11998e', borderWidth: 2 },
        },
        '& .MuiInputLabel-root.Mui-focused': { color: '#11998e' },
      }}
    >
      {select && options.map(opt => (
        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
      ))}
    </TextField>
  </Box>
);

export default Employees;
